// Users page — list admins + engineers + partners, create new admins,
// deactivate (self-protected).

import { useEffect, useState, type FormEvent } from "react";
import { gql } from "../lib/gql";
import { getUser } from "../lib/auth";

type AdminUser = {
  id: string;
  email: string;
  name: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
};

const LIST_QUERY = `
  query AdminUsers($role: String) {
    adminUsers(limit: 200, role: $role) {
      id
      email
      name
      role
      isActive
      createdAt
      lastLoginAt
    }
  }
`;

const CREATE_ADMIN_MUTATION = `
  mutation AdminCreateAdminUser($input: AdminCreateAdminUserInput!) {
    adminCreateAdminUser(input: $input) {
      id
      email
    }
  }
`;

const DEACTIVATE_MUTATION = `
  mutation AdminDeactivateUser($userId: UUID!) {
    adminDeactivateUser(userId: $userId)
  }
`;

export function UsersPage() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState<string>("");
  const [showCreate, setShowCreate] = useState(false);
  const me = getUser();

  async function reload() {
    try {
      const data = await gql<{ adminUsers: AdminUser[] }>(LIST_QUERY, {
        role: roleFilter || null,
      });
      setUsers(data.adminUsers);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  useEffect(() => {
    reload();
  }, [roleFilter]);

  async function handleDeactivate(u: AdminUser) {
    if (u.id === me?.id) {
      alert("Нельзя деактивировать себя — логин умрёт.");
      return;
    }
    if (!window.confirm(`Деактивировать ${u.email}?`)) return;
    try {
      await gql(DEACTIVATE_MUTATION, { userId: u.id });
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Пользователи ({users.length})</h2>
        <div className="row">
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            style={{
              background: "var(--bg-elevated)",
              color: "var(--text)",
              border: "1px solid var(--border)",
              padding: "6px 10px",
              borderRadius: 4,
            }}
          >
            <option value="">Все роли</option>
            <option value="admin">admin</option>
            <option value="engineer">engineer</option>
            <option value="partner">partner</option>
          </select>
          <button onClick={() => setShowCreate(true)}>+ Admin</button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {showCreate && (
        <CreateAdminForm
          onCancel={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            reload();
          }}
        />
      )}

      <div className="card">
        {users.length === 0 ? (
          <div className="muted">Нет пользователей под фильтр.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Имя</th>
                <th>Роль</th>
                <th>Статус</th>
                <th>Последний вход</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>{u.email}</td>
                  <td>{u.name}</td>
                  <td>
                    <span className={`badge ${u.role}`}>{u.role}</span>
                  </td>
                  <td>
                    {u.isActive ? (
                      <span className="badge engineer">active</span>
                    ) : (
                      <span className="badge inactive">inactive</span>
                    )}
                  </td>
                  <td className="muted">
                    {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString("ru-RU") : "—"}
                  </td>
                  <td>
                    {u.isActive && u.id !== me?.id && (
                      <button
                        className="secondary"
                        style={{ fontSize: 12, padding: "4px 8px" }}
                        onClick={() => handleDeactivate(u)}
                      >
                        Деактивировать
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function CreateAdminForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: () => void;
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await gql(CREATE_ADMIN_MUTATION, {
        input: {
          email: email.trim(),
          name: name.trim(),
          password,
        },
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <form className="card" onSubmit={onSubmit} style={{ background: "var(--bg-elevated)" }}>
      <h3 style={{ margin: "0 0 12px 0" }}>Новый admin</h3>

      {error && <div className="error-banner">{error}</div>}

      <div className="form-row">
        <label>Email *</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className="form-row">
        <label>Имя *</label>
        <input type="text" required value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="form-row">
        <label>Пароль *</label>
        <input
          type="password"
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          Минимум 8 символов. Новый admin сможет сменить пароль сам после логина.
        </div>
      </div>

      <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
        <button type="button" className="secondary" onClick={onCancel} disabled={loading}>
          Отмена
        </button>
        <button type="submit" disabled={loading}>
          {loading ? "Создаём…" : "Создать"}
        </button>
      </div>
    </form>
  );
}
